import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { useAuth } from '../../../contexts/AuthContext.js';

const GroupAdmin = () => {
  const { currentUser } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalGroups, setTotalGroups] = useState(0);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    groupname: '',
    groupdescription: '',
    groupnotes: '',
    groupowner: [],
    divergroup: []
  });
  
  // User search states
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Fetch groups on component mount
  useEffect(() => {
    if (currentUser) {
      fetchGroups();
    }
  }, [currentUser, page, rowsPerPage]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const fetchGroups = async () => {
    if (!currentUser) return;
    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/divergroups?page=${page + 1}&limit=${rowsPerPage}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch groups');
      }

      const data = await response.json();
      setGroups(Array.isArray(data.groups) ? data.groups : []);
      setTotalGroups(data.totalGroups || 0);
    } catch (err) {
      setError(`Error fetching groups: ${err.message}`);
      console.error('Error fetching groups:', err);
      setGroups([]);
      setTotalGroups(0);
    } finally {
      setLoading(false);
    }
  };

 const handleCreateGroup = async () => {
  if (!currentUser) return;
  
  // Clear previous errors
  setError(null);
  
  // Validation
  if (!formData.groupname.trim()) {
    setError('Group name is required');
    return;
  }
  
  if (formData.groupowner.length === 0) {
    setError('Please search for and select at least one group owner');
    return;
  }
  
  if (formData.divergroup.length === 0) {
    setError('Please search for and select at least one group member');
    return;
  }

  // Rest of your create function...
};

const searchUsers = async (searchTerm) => {
  if (!currentUser) {
    setUserSearchResults([]);
    return;
  }
  
  if (!searchTerm || searchTerm.trim().length < 2) {
    setUserSearchResults([]);
    return;
  }

  setUserSearchLoading(true);
  try {
    const token = await currentUser.getIdToken();
    const response = await fetch('/api/divers/search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        searchTerm: searchTerm.trim()
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to search users');
    }

    const users = await response.json();
    setUserSearchResults(Array.isArray(users) ? users : []);
  } catch (err) {
    console.error('Error searching users:', err);
    setError(`Error searching users: ${err.message}`);
    setUserSearchResults([]);
  } finally {
    setUserSearchLoading(false);
  }
};


  const handleUpdateGroup = async () => {
    if (!currentUser || !selectedGroup) return;
    
    // Validation
    if (!formData.groupname.trim()) {
      setError('Group name is required');
      return;
    }
    
    if (formData.groupowner.length === 0) {
      setError('At least one group owner is required');
      return;
    }
    
    if (formData.divergroup.length === 0) {
      setError('At least one group member is required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/divergroups/${selectedGroup._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          groupname: formData.groupname.trim(),
          groupdescription: formData.groupdescription.trim(),
          groupnotes: formData.groupnotes.trim(),
          groupowner: formData.groupowner.map(user => user.diverId || user.mongoData?._id || user.diverRecord?._id).filter(Boolean),
          divergroup: formData.divergroup.map(user => user.diverId || user.mongoData?._id || user.diverRecord?._id).filter(Boolean)
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update group');
      }

      setSuccess('Group updated successfully');
      setEditDialogOpen(false);
      resetForm();
      fetchGroups();
    } catch (err) {
      setError(`Error updating group: ${err.message}`);
      console.error('Error updating group:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!currentUser || !selectedGroup) return;

    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/divergroups/${selectedGroup._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete group');
      }

      setSuccess('Group deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedGroup(null);
      fetchGroups();
    } catch (err) {
      setError(`Error deleting group: ${err.message}`);
      console.error('Error deleting group:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      groupname: '',
      groupdescription: '',
      groupnotes: '',
      groupowner: [],
      divergroup: []
    });
    setSelectedUsers([]);
    setUserSearchResults([]);
  };

  const openCreateDialog = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const openEditDialog = (group) => {
    setSelectedGroup(group);
    setFormData({
      groupname: group.groupname || '',
      groupdescription: group.groupdescription || '',
      groupnotes: group.groupnotes || '',
      groupowner: group.groupowner || [],
      divergroup: group.divergroup || []
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (group) => {
    setSelectedGroup(group);
    setDeleteDialogOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const removeUserFromList = (userList, userToRemove, setUserList) => {
    const updatedList = userList.filter(user => 
      (user.uid || user._id) !== (userToRemove.uid || userToRemove._id)
    );
    setUserList(updatedList);
  };

  if (!currentUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Group Management
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Create Group
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <TableContainer component={Paper} sx={{ maxHeight: 440 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Group Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Members</TableCell>
                <TableCell>Owners</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No groups found
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((group) => (
                  <TableRow key={group._id} hover>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {group.groupname}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {group.groupdescription || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${group.divergroup?.length || 0} members`} 
                        size="small" 
                        color="primary" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${group.groupowner?.length || 0} owners`} 
                        size="small" 
                        color="secondary" 
                      />
                    </TableCell>
                    <TableCell>{formatDate(group.createdAt)}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit Group">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openEditDialog(group)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Group">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeleteDialog(group)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalGroups}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Create Group Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Group</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Name *"
                value={formData.groupname}
                onChange={(e) => setFormData({ ...formData, groupname: e.target.value })}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Description"
                value={formData.groupdescription}
                onChange={(e) => setFormData({ ...formData, groupdescription: e.target.value })}
                variant="outlined"
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Notes"
                value={formData.groupnotes}
                onChange={(e) => setFormData({ ...formData, groupnotes: e.target.value })}
                variant="outlined"
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Group Owners *
              </Typography>
              <Autocomplete
                multiple
                options={userSearchResults}
                getOptionLabel={(option) => `${option.email} (${option.displayName || 'No name'})`}
                value={formData.groupowner}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, groupowner: newValue });
                }}
                onInputChange={(event, newInputValue) => {
                  searchUsers(newInputValue);
                }}
                loading={userSearchLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search and select group owners"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {userSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.email}
                      {...getTagProps({ index })}
                      key={option.uid || option._id}
                    />
                  ))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Group Members *
              </Typography>
              <Autocomplete
                multiple
                options={userSearchResults}
                getOptionLabel={(option) => `${option.email} (${option.displayName || 'No name'})`}
                value={formData.divergroup}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, divergroup: newValue });
                }}
                onInputChange={(event, newInputValue) => {
                  searchUsers(newInputValue);
                }}
                loading={userSearchLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search and select group members"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {userSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.email}
                      {...getTagProps({ index })}
                      key={option.uid || option._id}
                    />
                  ))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateGroup} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Group</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Name *"
                value={formData.groupname}
                onChange={(e) => setFormData({ ...formData, groupname: e.target.value })}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Description"
                value={formData.groupdescription}
                onChange={(e) => setFormData({ ...formData, groupdescription: e.target.value })}
                variant="outlined"
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Notes"
                value={formData.groupnotes}
                onChange={(e) => setFormData({ ...formData, groupnotes: e.target.value })}
                variant="outlined"
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Group Owners *
              </Typography>
              <Autocomplete
                multiple
                options={userSearchResults}
                getOptionLabel={(option) => `${option.email} (${option.displayName || 'No name'})`}
                value={formData.groupowner}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, groupowner: newValue });
                }}
                onInputChange={(event, newInputValue) => {
                  searchUsers(newInputValue);
                }}
                loading={userSearchLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search and select group owners"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {userSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.email}
                      {...getTagProps({ index })}
                      key={option.uid || option._id}
                    />
                  ))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Group Members *
              </Typography>
              <Autocomplete
                multiple
                options={userSearchResults}
                getOptionLabel={(option) => `${option.email} (${option.displayName || 'No name'})`}
                value={formData.divergroup}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, divergroup: newValue });
                }}
                onInputChange={(event, newInputValue) => {
                  searchUsers(newInputValue);
                }}
                loading={userSearchLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search and select group members"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {userSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.email}
                      {...getTagProps({ index })}
                      key={option.uid || option._id}
                    />
                  ))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateGroup} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Update Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the group "{selectedGroup?.groupname}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteGroup} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupAdmin;
